import { useEffect, useState } from "react";
import { Wand2, Trash2, Mail, Phone, User, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError, API } from "@/lib/api";
import Modal from "@/components/admin/Modal";
import { PageHeader } from "./AdminFaq";

const BASE = API.replace("/api", "");

export default function AdminBrandingProposals() {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selected, setSelected] = useState(null);

    async function load() {
        setLoading(true);
        try { const { data } = await api.get("/admin/branding-proposals"); setItems(data || []); }
        catch (err) { toast.error(formatApiError(err?.response?.data?.detail) || "Erro ao carregar"); }
        finally { setLoading(false); }
    }
    useEffect(() => { load(); }, []);

    async function onDelete(item) {
        if (!window.confirm("Apagar esta proposta?")) return;
        try { await api.delete(`/admin/branding-proposals/${item.id}`); toast.success("Apagada"); setSelected(null); load(); }
        catch { toast.error("Erro ao apagar"); }
    }

    return (
        <div data-testid="admin-branding-proposals-page">
            <PageHeader title="Propostas de branding" subtitle="Propostas visuais geradas por IA no Simulador, com o logo e o contacto de quem as criou" count={items.length} />

            <div className="mt-8">
                {loading ? (
                    <div className="rounded-2xl border border-border bg-white p-12 text-center text-muted-foreground">A carregar...</div>
                ) : items.length === 0 ? (
                    <div className="rounded-2xl border border-border bg-white p-12 text-center text-muted-foreground">
                        <Wand2 className="h-8 w-8 mx-auto mb-3 opacity-40" />
                        Ainda sem propostas. Quando um visitante gerar uma proposta visual no Simulador, aparece aqui.
                    </div>
                ) : (
                    <div className="grid gap-5 sm:grid-cols-2 xl:grid-cols-3">
                        {items.map((item) => (
                            <div key={item.id} data-testid={`branding-proposal-card-${item.id}`}
                                className="group overflow-hidden rounded-2xl border border-border bg-white transition hover:shadow-lg">
                                <button type="button" onClick={() => setSelected(item)} className="block w-full">
                                    <img src={`${BASE}${item.image_url}`} alt={item.brand_name || "Proposta"} loading="lazy"
                                        className="aspect-[3/2] w-full object-cover transition group-hover:scale-[1.02]" />
                                </button>
                                <div className="flex items-start gap-3 p-4">
                                    {item.logo_url ? (
                                        <img src={`${BASE}${item.logo_url}`} alt="logo" className="h-11 w-11 shrink-0 rounded-md border border-border bg-white object-contain p-1" />
                                    ) : (
                                        <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-md border border-border bg-muted"><Wand2 className="h-4 w-4 text-muted-foreground" /></div>
                                    )}
                                    <div className="min-w-0 flex-1">
                                        <p className="truncate text-sm font-bold text-primary">{item.brand_name || "Sem nome de marca"}</p>
                                        <p className="text-[11px] text-muted-foreground">
                                            Modelo {item.model === "alfa" ? "Alfa" : "Ômega"} · {(item.created_at || "").slice(0, 16).replace("T", " ")}
                                        </p>
                                        {item.contact ? (
                                            <div className="mt-1.5 space-y-0.5 text-xs text-primary" data-testid={`proposal-contact-${item.id}`}>
                                                {item.contact.name && <div className="inline-flex items-center gap-1.5 font-semibold"><User className="h-3 w-3 text-secondary" /> {item.contact.name}</div>}
                                                {item.contact.email && <div className="flex items-center gap-1.5"><Mail className="h-3 w-3 text-secondary" /> <span className="truncate">{item.contact.email}</span></div>}
                                                {item.contact.phone && <div className="flex items-center gap-1.5"><Phone className="h-3 w-3 text-secondary" /> {item.contact.phone}</div>}
                                            </div>
                                        ) : (
                                            <p className="mt-1.5 inline-flex rounded-md bg-zinc-100 px-2 py-0.5 text-[10px] font-bold text-zinc-500">Sem contacto (não submeteu)</p>
                                        )}
                                    </div>
                                    <button onClick={() => onDelete(item)} data-testid={`proposal-delete-${item.id}`} title="Apagar"
                                        className="rounded-md p-2 text-red-600 hover:bg-red-50"><Trash2 className="h-4 w-4" /></button>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            <Modal open={!!selected} onClose={() => setSelected(null)} title={selected?.brand_name || "Proposta de branding"} size="lg">
                {selected && (
                    <div data-testid="proposal-detail-modal">
                        <img src={`${BASE}${selected.image_url}`} alt={selected.brand_name || "Proposta"} className="w-full rounded-xl border border-border" />
                        <div className="mt-4 flex flex-wrap items-center justify-between gap-3">
                            <div className="text-xs text-muted-foreground">
                                Modelo {selected.model === "alfa" ? "Alfa" : "Ômega"} · {(selected.created_at || "").slice(0, 16).replace("T", " ")}
                                {selected.contact?.email && <> · <span className="font-semibold text-primary">{selected.contact.email}</span></>}
                            </div>
                            <div className="flex gap-2">
                                {selected.config_id && (
                                    <a href={`/c/${selected.config_id}`} target="_blank" rel="noreferrer" data-testid="proposal-open-config"
                                        className="inline-flex items-center gap-1.5 rounded-md border border-border px-3 py-2 text-xs font-bold text-primary hover:bg-muted">
                                        <ExternalLink className="h-3.5 w-3.5" /> Ver configuração
                                    </a>
                                )}
                                <button onClick={() => onDelete(selected)} className="inline-flex items-center gap-1.5 rounded-md bg-red-50 px-3 py-2 text-xs font-bold text-red-600 hover:bg-red-100">
                                    <Trash2 className="h-3.5 w-3.5" /> Apagar
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </Modal>
        </div>
    );
}
